import {
  DeleteOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { ClubCrudApi } from '@hefe/store';
import {
  Button,
  Popconfirm,
  Popover,
  Space,
  Tooltip,
  Upload,
  message,
} from 'antd';
import { RcFile } from 'antd/lib/upload';
import { environment } from '../../../../../environments/environment';

interface ImageUploadCellProps {
  /** Current image path/URL */
  value?: string;
  /** Resource name (e.g. 'partners') */
  resource: string;
  /** Item ID */
  itemId: number;
  /** Field/attribute name for the file (e.g. 'image', 'logo') */
  fieldName: string;
}

/**
 * Image Upload Cell Component for Table
 *
 * Uses the file upload endpoints:
 * - POST /crud/club/{resource}/{id}/upload/{attribute}
 * - DELETE /crud/club/{resource}/{id}/upload/{attribute}
 */
export default function ImageUploadCell({
  value,
  resource,
  itemId,
  fieldName,
}: ImageUploadCellProps) {
  const [uploadFile, { isLoading: isUploading }] =
    ClubCrudApi.useUploadFileMutation();
  const [deleteFile, { isLoading: isDeleting }] =
    ClubCrudApi.useDeleteFileMutation();

  const isLoading = isUploading || isDeleting;

  const beforeUpload = (file: RcFile) => {
    const isValidType =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/webp';

    if (!isValidType) {
      message.error(t('errors.avatar_upload.wrong_format'));
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error(t('errors.avatar_upload.too_large'));
      return false;
    }

    handleUpload(file);
    return false;
  };

  const handleUpload = async (file: RcFile) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      await uploadFile({
        resource,
        id: itemId,
        attribute: fieldName,
        file: formData,
      }).unwrap();

      message.success(t('success.upload'));
    } catch (error) {
      message.error(t('errors.upload_failed'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFile({
        resource,
        id: itemId,
        attribute: fieldName,
      }).unwrap();

      message.success(t('success.deleted'));
    } catch (error) {
      message.error(t('errors.delete_failed'));
    }
  };

  // Construct full URL if value is a relative path
  const imageUrl = value?.startsWith('http')
    ? value
    : value
    ? `${environment.serverUrl}/storage/${value}`
    : null;

  if (isLoading) {
    return <LoadingOutlined style={{ fontSize: 20 }} />;
  }

  return (
    <Space size={8} onClick={(e) => e.stopPropagation()}>
      {imageUrl ? (
        <>
          <Popover
            trigger="click"
            content={
              <img
                src={imageUrl}
                alt=""
                style={{ maxHeight: 400, maxWidth: 800, objectFit: 'contain' }}
              />
            }
          >
            <img
              src={imageUrl}
              alt=""
              style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }}
            />
          </Popover>
          <Tooltip title={t('upload.image.edit')}>
            <Upload
              accept=".jpg,.jpeg,.png,.webp"
              showUploadList={false}
              beforeUpload={beforeUpload}
              disabled={isLoading}
            >
              <Button size="small" icon={<UploadOutlined />} />
            </Upload>
          </Tooltip>
          <Popconfirm
            title={t('questions.delete')}
            onConfirm={handleDelete}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Tooltip title={t('delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </>
      ) : (
        <Upload
          accept=".jpg,.jpeg,.png,.webp"
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={isLoading}
        >
          <Button size="small" icon={<UploadOutlined />}>
            {t('crud.image.upload')}
          </Button>
        </Upload>
      )}
    </Space>
  );
}
